import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { getParties } from '@/app/services/partyService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileSearchInput } from '@/components/ui/MobileSearchInput';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import type { Party } from '@/app/types/party';

interface PartyPickerProps {
    open: boolean;
    onClose: () => void;
    onSelect: (party: Party) => void;
    selectedPartyId?: number | null;
}

export function PartyPicker({ open, onClose, onSelect, selectedPartyId }: PartyPickerProps) {
    const [rawQuery, setRawQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const user = useAuthStore((state) => state.user);
    const responsiblePartyId = user?.managed_parties?.[0]?.id;

    useEffect(() => {
        if (!open) {
            setRawQuery('');
            setDebouncedQuery('');
            return;
        }
        const t = setTimeout(() => inputRef.current?.focus(), 220);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(rawQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [rawQuery]);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['partyPicker', debouncedQuery, responsiblePartyId],
        queryFn: () => getParties(50, 0, debouncedQuery || undefined, responsiblePartyId),
        enabled: open,
        staleTime: 30_000,
    });

    const parties = data?.items ?? [];

    return (
        <MobileBottomSheet
            open={open}
            onClose={onClose}
            title="Select Customer"
            heightMode="tall"
        >
            <div className="flex flex-col h-full">
                <div className="p-4 flex-shrink-0 border-b border-border">
                    <MobileSearchInput
                        ref={inputRef}
                        value={rawQuery}
                        onValueChange={setRawQuery}
                        placeholder="Search customers..."
                    />
                </div>

                <div className="flex-1 overflow-y-auto">
                    {isLoading || (isFetching && parties.length === 0) ? (
                        <PickerSkeleton />
                    ) : parties.length === 0 ? (
                        <MobileEmptyState
                            icon={<Search className="h-12 w-12" />}
                            title={debouncedQuery ? 'No matches' : 'No customers'}
                            description={
                                debouncedQuery
                                    ? 'Try a different name or phone number'
                                    : 'You have no customers assigned'
                            }
                        />
                    ) : (
                        <ul className="divide-y divide-border">
                            {parties.map((party) => {
                                const isSelected = party.id === selectedPartyId;
                                const unallocatedCredit = Number(party.unallocated_credit || 0);
                                return (
                                    <li key={party.id}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onSelect(party);
                                                onClose();
                                            }}
                                            className="w-full flex items-center gap-3 p-4 min-h-[64px] text-left active:bg-accent transition-colors"
                                        >
                                            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <span className="text-base font-semibold text-primary">
                                                    {party.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-foreground truncate">
                                                    {party.name}
                                                </p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    {party.phone && (
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            {party.phone}
                                                        </p>
                                                    )}
                                                    {unallocatedCredit > 0 && (
                                                        <span className="text-xs text-green-500 font-medium whitespace-nowrap">
                                                            Credit ₹{unallocatedCredit.toLocaleString('en-IN')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <span className="text-primary text-xs font-semibold flex-shrink-0">
                                                    SELECTED
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </MobileBottomSheet>
    );
}

function PickerSkeleton() {
    return (
        <ul className="divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
                <li key={i} className="flex items-center gap-3 p-4 min-h-[64px]">
                    <MobileSkeleton className="w-11 h-11 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <MobileSkeleton className="h-4 w-32" />
                        <MobileSkeleton className="h-3 w-24" />
                    </div>
                </li>
            ))}
        </ul>
    );
}
